import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { FormLabel, MButton } from 'components'
import { color } from 'config/color'
import { cloneDeep, isEmpty } from 'lodash'
import type React from 'react'
import { useEffect, useState } from 'react'
import { isAuthorized, useMobileBreakpoints, useProvideAuth } from 'utils'

interface EditAssetCarouselProps {
  isOpen: boolean
  onClose: () => void
  failedFiles: any
  fileData: any
}

export const EditAssetCarousel: React.FC<EditAssetCarouselProps> = (
  props: EditAssetCarouselProps
) => {
  const { uploadAssets, handleLogOut } = useProvideAuth()
  const isMobile = useMobileBreakpoints()

  const [newFileNames, setNewFileNames] = useState<any>(
    props.failedFiles.map((item: any) => item.filename)
  )

  const [count, setCount] = useState<number>(0)
  const [imageUrl, setImageUrl] = useState<string>()
  const [uploadedCount, setUploadedCount] = useState<number>(0)
  const [discardedCount, setDiscardedCount] = useState<number>(0)
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)

  useEffect(() => {
    setNewFileNames(props.failedFiles.map((item: any) => item.filename))
  }, [props.failedFiles, props.fileData, props.isOpen])

  useEffect(() => {
    if (count < props.failedFiles.length) {
      setImageUrl(URL.createObjectURL(props.fileData[count]))
    }
  }, [count, props.failedFiles.length, props.fileData])

  const handleClose = (_event: any, reason: string): void => {
    if (reason === 'backdropClick') return
    props.onClose()
  }

  const handleUpdateAndUpload = async (): Promise<void> => {
    try {
      const fileToBeUpdated = Array.from(props.fileData).find(
        (item: any) => item.name === props.failedFiles[count].filename
      )

      const newFile = new File(
        [fileToBeUpdated as BlobPart],
        newFileNames[count]
      )

      const formData = new FormData()
      formData.append('assets', newFile)
      const response = await uploadAssets(formData)

      const failedFiles = response.result.filter(
        (item: any) =>
          item.status === 'FAILED' && item.error === 'Duplicate Asset Name'
      )

      if (!isEmpty(failedFiles)) {
        setIsDuplicate(true)
        return
      }

      setCount(count + 1)
      setUploadedCount(uploadedCount + 1)
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()
      setCount(count + 1)
      setDiscardedCount(discardedCount + 1)
    }
  }

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="xs">
      {count !== props.failedFiles.length && (
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <ErrorIcon
                sx={{
                  color: color.error[500],
                  fontSize: 24,
                  padding: 0
                }}
              />
              <Typography
                fontSize={isMobile ? 20 : 22}
                fontWeight={500}
                color={color.black}
                display="inline"
              >
                {'Asset’s Name Conflict '}
              </Typography>
              <Typography
                fontSize={isMobile ? 20 : 22}
                fontWeight={400}
                color={color.grey[700]}
                display="inline"
              >
                {`(${count + 1}/${props.failedFiles.length})`}
              </Typography>
            </Box>
            <IconButton onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}
      <DialogContent sx={{ paddingTop: 0 }}>
        {count === props.failedFiles.length
          ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              alignContent="center"
              alignItems="center"
              pt={2}
            >
              <CheckCircleRoundedIcon
                sx={{
                  color: color.success[600],
                  backgroundColor: color.success[50],
                  fontSize: 60,
                  borderRadius: '50%',
                  padding: 3
                }}
              />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                alignContent="center"
              >
                <Typography
                  textAlign="center"
                  fontSize={24}
                  fontWeight={500}
                  color={color.grey[900]}
                  mb={1}
                >
                  {'Asset(s) has been saved'}
                </Typography>
                <Box>
                  <Typography
                    textAlign="center"
                    fontSize={16}
                    fontWeight={400}
                    color={color.grey[700]}
                    display="inline"
                  >
                    {'Uploaded: '}
                  </Typography>
                  <Typography
                    textAlign="center"
                    fontSize={16}
                    fontWeight={500}
                    color={color.grey[900]}
                    display="inline"
                  >
                    {`${uploadedCount} assets `}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    textAlign="center"
                    fontSize={16}
                    fontWeight={400}
                    color={color.grey[700]}
                    display="inline"
                  >
                    {'Discarded: '}
                  </Typography>

                  <Typography
                    textAlign="center"
                    fontSize={16}
                    fontWeight={500}
                    color={color.grey[900]}
                    display="inline"
                  >
                    {`${discardedCount} assets `}
                  </Typography>
                </Box>
              </Box>

              <MButton
                variant="contained"
                type="button"
                rounded="true"
                texttransform="none"
                onClick={props.onClose}
                fullWidth
              >
                Back to page
              </MButton>
            </Box>
          )
          : (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography fontSize={16} fontWeight={400} color={color.grey[700]}>
                {
                  'The following assets’s name have already been used, please provide a different one if you want to upload them or discard them'
                }
              </Typography>

              <Box
                component="img"
                display="flex"
                alignItems="center"
                src={imageUrl}
                alt="background"
              />

              <Box>
                <FormLabel>Enter Name</FormLabel>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="Enter Name"
                  onChange={(e) => {
                    const tempNewFileNames = cloneDeep(newFileNames)
                    tempNewFileNames[count] = e.target.value
                    setNewFileNames(tempNewFileNames)
                    setIsDuplicate(false)
                  }}
                  value={newFileNames[count]}
                  error={isDuplicate}
                  helperText={isDuplicate ? 'Asset names must be unique' : ''}
                />
              </Box>

              <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                gap={1}
                mt={2}
              >
                <MButton
                  variant="contained"
                  type="button"
                  rounded="true"
                  texttransform="none"
                  onClick={handleUpdateAndUpload}
                  disabled={
                    newFileNames[count] === props.failedFiles[count].filename
                  }
                >
                  Update and upload asset
                </MButton>
                <MButton
                  variant="outlined"
                  type="button"
                  rounded="true"
                  texttransform="none"
                  onClick={() => {
                    setCount(count + 1)
                    setDiscardedCount(discardedCount + 1)
                  }}
                >
                  Discard asset
                </MButton>
              </Box>
            </Box>
          )}
      </DialogContent>
    </Dialog>
  )
}
